const ADBUT_SDK_URL = 'https://www.adbut.com/sdk/adbut-ads.js'
const SDK_TIMEOUT_MS = 10000

let sdkPromise = null

function waitForAdbutApi(timeoutMs = SDK_TIMEOUT_MS) {
  if (globalThis.RoiifyAds?.show) return Promise.resolve(globalThis.RoiifyAds)

  return new Promise((resolve, reject) => {
    const startedAt = Date.now()
    const timer = setInterval(() => {
      if (globalThis.RoiifyAds?.show) {
        clearInterval(timer)
        resolve(globalThis.RoiifyAds)
        return
      }
      if (Date.now() - startedAt >= timeoutMs) {
        clearInterval(timer)
        reject(new Error('AdBut SDK did not expose RoiifyAds.show'))
      }
    }, 50)
  })
}

function loadAdbutSdk() {
  if (globalThis.RoiifyAds?.show) return Promise.resolve(globalThis.RoiifyAds)
  if (sdkPromise) return sdkPromise

  sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${ADBUT_SDK_URL}"]`)
    const script = existing || document.createElement('script')
    let settled = false
    const finish = (callback) => (value) => {
      if (settled) return
      settled = true
      callback(value)
    }
    const handleLoad = finish(() => {
      waitForAdbutApi().then(resolve, reject)
    })
    const handleError = finish(() => reject(new Error('AdBut SDK failed to load')))

    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })
    if (!existing) {
      script.src = ADBUT_SDK_URL
      script.async = true
      script.referrerPolicy = 'strict-origin-when-cross-origin'
      document.head.appendChild(script)
    } else {
      waitForAdbutApi().then(resolve, reject)
    }
  })

  return sdkPromise
}

class AdbutInterstitial {
  constructor(options = {}) {
    this.placementKey = String(options.placementKey || options.placementId || '').trim()
    this.format = 'in_app_interstitial'
    this.regionId = options.regionId || 'adbut-ad-region'
    this.slotId = options.slotId || 'adbut-ad-slot'
    this.apiBaseUrl = String(options.apiBaseUrl || '').trim()
    this.theme = ['auto', 'light', 'dark'].includes(options.theme) ? options.theme : 'auto'
    this.width = options.width === 'fixed' ? 'fixed' : 'auto'
    const radius = Number(options.radius)
    this.radius = Number.isFinite(radius) ? Math.max(0, Math.min(48, radius)) : 8
    this.region = document.getElementById(this.regionId)
    this.slot = document.getElementById(this.slotId)
    this.readyPromise = null
    this.showPromise = null
  }

  isConfigured() {
    return Boolean(this.placementKey && this.placementKey !== 'YOUR_PLACEMENT_KEY')
  }

  prepare() {
    if (!this.isConfigured() || !this.slot) return false

    // Keep the target out of the SDK's automatic DOM scan until the game is finished.
    this.slot.removeAttribute('data-roiify-placement')
    this.slot.dataset.format = this.format
    this.slot.dataset.theme = this.theme
    this.slot.dataset.width = this.width
    this.slot.dataset.radius = String(this.radius)
    this.readyPromise = loadAdbutSdk()
    // The game may never finish; keep a preload failure from becoming unhandled.
    this.readyPromise.catch(() => {})
    return true
  }

  async show() {
    if (!this.isConfigured() || !this.slot) {
      return { ok: false, reason: 'not_configured' }
    }
    if (this.showPromise) return this.showPromise

    this.showPromise = this.showOnce()
    return this.showPromise
  }

  async showOnce() {
    try {
      const sdk = await (this.readyPromise || loadAdbutSdk())
      this.slot.dataset.roiifyPlacement = this.placementKey
      this.slot.dataset.format = this.format
      this.slot.dataset.theme = this.theme
      this.slot.dataset.width = this.width
      this.slot.dataset.radius = String(this.radius)
      if (this.region) this.region.hidden = false
      const options = {
        theme: this.theme,
        width: this.width,
        radius: this.radius
      }
      if (this.apiBaseUrl) options.apiBaseUrl = this.apiBaseUrl
      const controller = await sdk.show(this.placementKey, this.slot, options)
      return { ok: true, controller }
    } catch (error) {
      console.warn('[AdBut] Interstitial unavailable:', error)
      return { ok: false, reason: 'load_failed', error }
    }
  }
}

module.exports = { AdbutInterstitial, loadAdbutSdk }
